import DeviceData from 'drivers/deviceData';
import { numKeys } from 'utils/common/numKeys';
import getIsRTLLanguage from 'utils/common/isRTL';

/**
 * Channel navigation utilities
 * Manages keyboard navigation, channel index calculation, and numeric channel selection
 */
export const createChannelNavigationService = () => {
  /**
   * Handle keypress events with RTL support.
   *
   * Directional keys fire axis-specific callbacks with a delta (+1 / -1):
   *   onVerticalMove(delta)   — UP (-1) / DOWN (+1)
   *   onHorizontalMove(delta) — LEFT (-1) / RIGHT (+1), already RTL-corrected via keyMapping
   *
   * Callers decide what each axis means (channel navigation, section switching, etc.).
   *
   * @param {Event}  event     - Keyboard event
   * @param {Object} state     - Current state { channelOnly, channelTimeOut }
   * @param {Object} callbacks - { onVerticalMove, onHorizontalMove, onEnter, onBack, onChannelNumber }
   */
  const handleKeyPress = async (event, state, callbacks) => {
    const tvKeyControls = await DeviceData.getTVControl();
    const channelKeys = numKeys(tvKeyControls);
    const isRTL = getIsRTLLanguage();

    const keyMapping = {
      [tvKeyControls.KEY_LEFT]: isRTL ? tvKeyControls.KEY_RIGHT : tvKeyControls.KEY_LEFT,
      [tvKeyControls.KEY_RIGHT]: isRTL ? tvKeyControls.KEY_LEFT : tvKeyControls.KEY_RIGHT,
      [tvKeyControls.KEY_UP]: tvKeyControls.KEY_UP,
      [tvKeyControls.KEY_DOWN]: tvKeyControls.KEY_DOWN,
    };

    const { channelOnly, channelTimeOut } = state;

    switch (event.keyCode) {
      case keyMapping[tvKeyControls.KEY_RIGHT]:
        event.preventDefault();
        callbacks.onHorizontalMove?.(+1);
        break;

      case keyMapping[tvKeyControls.KEY_LEFT]:
        event.preventDefault();
        callbacks.onHorizontalMove?.(-1);
        break;

      case keyMapping[tvKeyControls.KEY_DOWN]:
        event.preventDefault();
        callbacks.onVerticalMove?.(+1);
        break;

      case keyMapping[tvKeyControls.KEY_UP]:
        event.preventDefault();
        callbacks.onVerticalMove?.(-1);
        break;

      case tvKeyControls.KEY_ENTER:
        callbacks.onEnter?.();
        break;

      case tvKeyControls.KEY_BACK:
        callbacks.onBack?.(channelOnly);
        event.preventDefault();
        break;

      default:
        // Handle numeric channel selection
        clearTimeout(channelTimeOut);
        if (channelKeys[event.keyCode] > -1) {
          callbacks.onChannelNumber?.(event.keyCode);
        }
        break;
    }
  };

  /**
   * Wrap channel index with circular buffer logic
   * @param {number} newIndex - Desired index
   * @param {number} totalItems - Total number of items
   * @returns {number} Wrapped index
   */
  const wrapChannelIndex = (newIndex, totalItems) => {
    if (totalItems <= newIndex) return 0;
    if (newIndex < 0) return totalItems - 1;
    return newIndex;
  };

  /**
   * Convert numeric key array to channel index
   * e.g., [1, 2, 3] -> 123
   */
  const getChannelIndexFromNumbers = (numberArray) => {
    return Number(numberArray.join(''));
  };

  return {
    handleKeyPress,
    wrapChannelIndex,
    getChannelIndexFromNumbers,
  };
};
